import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, FileText } from 'lucide-react';
import { getProducts } from '../services/api';
import DEMO_PRODUCTS from '../data/demoProducts';

const VIGNETTE = 'linear-gradient(135deg, rgba(22,21,18,0.72) 0%, rgba(22,21,18,0.38) 55%, rgba(22,21,18,0.18) 100%)';

const PLACEHOLDER_COLORS = {
  'Southern Oak':   '#d4b896',
  'Maple Forest':   '#c8b89a',
  'Walnut Grove':   '#8b6c4f',
  'Earth Stone':    '#a09080',
  'EIR Collection': '#b8a898',
  'EIR':            '#b8a898',
};

function CollectionRow({ product, index, isOpen, onToggle }) {
  const bgImage = product.image_urls?.[0];
  const bgColor = PLACEHOLDER_COLORS[product.name] || '#c8b89a';

  return (
    <div
      style={{
        borderBottom: "1px solid rgba(22,21,18,.12)",
        overflow: "hidden",
      }}>
      {/* Label / trigger — uses .collection-row-btn for responsive padding */}
      <button
        onClick={onToggle}
        className="collection-row-btn"
        style={{
          height: isOpen ? 220 : 140,
          transition: "height 0.45s cubic-bezier(.4,0,.2,1)",
        }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: bgImage
              ? `url(${bgImage}) center/cover no-repeat`
              : bgColor,
            transform: isOpen ? "scale(1.04)" : "scale(1)",
            transition: "transform 0.5s cubic-bezier(.4,0,.2,1)",
          }}
        />
        <div style={{ position: "absolute", inset: 0, background: VIGNETTE }} />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "baseline",
            gap: 24,
          }}>
          <span
            style={{
              fontFamily: "var(--mono)",
              fontSize: 11,
              letterSpacing: "0.22em",
              color: "rgba(255,255,255,0.55)",
            }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <div>
            <h2
              style={{
                fontFamily: "var(--serif)",
                fontSize: "clamp(22px, 3.8vw, 48px)",
                fontWeight: 400,
                color: "#fff",
                lineHeight: 1.05,
                textShadow: "0 2px 12px rgba(22,21,18,0.4)",
              }}>
              {product.name}
            </h2>
            {product.category && (
              <p
                style={{
                  fontFamily: "var(--sans)",
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.65)",
                  marginTop: 7,
                }}>
                {product.category}
              </p>
            )}
          </div>
        </div>

        <ChevronDown
          size={22}
          style={{
            position: "relative",
            zIndex: 2,
            color: "rgba(255,255,255,0.75)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.35s cubic-bezier(.4,0,.2,1)",
            flexShrink: 0,
          }}
        />
      </button>

      {/* Expanded body — uses .collection-row-body + .collection-row-body-inner */}
      <div
        className="collection-row-body"
        style={{ maxHeight: isOpen ? 600 : 0 }}>
        <div className="collection-row-body-inner">
          <div>
            <p
              style={{
                fontFamily: "var(--sans)",
                fontSize: 15,
                color: "var(--ink-soft)",
                lineHeight: 1.75,
                marginBottom: 22,
              }}>
              {product.description}
            </p>
            {product.specs && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {product.specs.thickness && (
                  <span className="chip">
                    {product.specs.thickness}mm thick
                  </span>
                )}
                {product.specs.wear_layer && (
                  <span className="chip">
                    {product.specs.wear_layer} wear layer
                  </span>
                )}
                {product.specs.waterproof && (
                  <span className="chip">100% Waterproof</span>
                )}
                {product.specs.surface_texture && (
                  <span className="chip">{product.specs.surface_texture}</span>
                )}
                {product.specs.locking_system && (
                  <span className="chip">{product.specs.locking_system}</span>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p
              style={{
                fontFamily: "var(--sans)",
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--ash)",
                marginBottom: 4,
              }}>
              Interested in this collection?
            </p>
            <Link
              to={`/products/${product._id}`}
              className="btn btn-primary btn-lg"
              style={{ justifyContent: "center", borderRadius: "var(--r-sm)" }}>
              <FileText size={14} /> Request a Price Quote
            </Link>
            <p
              style={{
                fontFamily: "var(--sans)",
                fontSize: 11,
                color: "var(--ash)",
                textAlign: "center",
                lineHeight: 1.5,
              }}>
              Wholesale pricing · 1–2 business day response
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [openIdx,  setOpenIdx]  = useState(null);

  useEffect(() => {
    getProducts()
      .then(res => setProducts(res.data.products || res.data || []))
      .catch(() => setProducts(DEMO_PRODUCTS))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (i) => setOpenIdx(prev => prev === i ? null : i);

  const heroImg = products[0]?.image_urls?.[0] || null;

  return (
    <main style={{ paddingTop: 76 }}>
      <div id="refresh-top" />

      {/* ── Page header ── */}
      <section
        className="catalog-hero"
        style={{
          position: "relative",
          padding: "80px 0 64px",
          overflow: "hidden",
          background: heroImg ? "var(--ink)" : "var(--ivory-soft)",
          minHeight: 340,
          display: "flex",
          alignItems: "center",
        }}>
        {heroImg && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${heroImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.35,
            }}
          />
        )}
        {heroImg && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(100deg, rgba(22,21,18,0.88) 0%, rgba(22,21,18,0.55) 60%, rgba(22,21,18,0.15) 100%)",
            }}
          />
        )}

        <div
          className="page-container"
          style={{ position: "relative", zIndex: 2 }}>
          <p
            className="eyebrow eyebrow-gold animate-fade-up"
            style={{
              marginBottom: 16,
              color: heroImg ? "var(--saffron)" : undefined,
            }}>
            Wholesale Catalog
          </p>
          <h1
            className="animate-fade-up delay-1"
            style={{
              fontFamily: "var(--serif)",
              fontSize: "clamp(32px, 6.5vw, 88px)",
              textWrap: "balance",
              lineHeight: 1,
              maxWidth: 820,
              color: heroImg ? "#fff" : "var(--ink)",
            }}>
            Premium collections.{" "}
            <em style={{ fontWeight: 300, color: "var(--saffron)" }}>
              Dozens of options.
            </em>
          </h1>
          <p
            className="animate-fade-up delay-2"
            style={{
              fontFamily: "var(--sans)",
              fontSize: 17,
              color: heroImg ? "rgba(255,255,255,0.75)" : "var(--ink-soft)",
              lineHeight: 1.65,
              maxWidth: 520,
              marginTop: 20,
            }}>
            Every plank is 100% waterproof SPC, click-lock, and priced for
            volume. Pick your direction below and request a wholesale quote.
          </p>
          {/* <div className="animate-fade-up delay-3" style={{ marginTop: 28 }}>
            <Link
              to="/marketplace"
              className="btn-arrow"
              style={{
                color: heroImg ? "var(--saffron)" : "var(--saffron-deep)",
              }}>
              Want to buy direct? Visit the Marketplace{" "}
              <ArrowRight
                size={14}
                style={{ display: "inline", verticalAlign: "middle" }}
              />
            </Link>
          </div> */}
        </div>
      </section>

      {/* ── Accordion ── */}
      <section
        style={{ maxWidth: 1140, margin: "0 auto", padding: "0 0 96px" }}>
        {loading ? (
          <div style={{ padding: "48px 20px" }}>
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="skeleton"
                style={{ height: 140, marginBottom: 2, borderRadius: 0 }}
              />
            ))}
          </div>
        ) : (
          <div style={{ borderTop: "1px solid rgba(22,21,18,.12)" }}>
            {products.map((product, i) => (
              <CollectionRow
                key={product._id}
                product={product}
                index={i}
                isOpen={openIdx === i}
                onToggle={() => toggle(i)}
              />
            ))}
          </div>
        )}

        {!loading && (
          <div style={{ padding: "64px 20px 0", textAlign: "center" }}>
            <p
              style={{
                fontFamily: "var(--sans)",
                fontSize: 14,
                color: "var(--ash)",
                marginBottom: 20,
              }}>
              Ready to price a full project? Submit a single RFQ covering all
              collections.
            </p>
            <Link
              to="/rfq/submit"
              className="btn btn-saffron btn-lg"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FileText size={14} /> Request Wholesale Pricing
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
